import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '../App';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { BookOpen, Clock, Star, Award, Check, ArrowRight, TrendingUp, DollarSign, Briefcase, GraduationCap, Target, Users } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL 
  ? `${process.env.REACT_APP_BACKEND_URL}/api` 
  : 'http://localhost:8001/api';

const Courses = () => {
  const navigate = useNavigate();
  const { token, user } = useContext(AuthContext);
  const [courses, setCourses] = useState([]);
  const [enrolledCourses, setEnrolledCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [enrolling, setEnrolling] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');

  useEffect(() => {
    if (!token) {
      navigate('/login');
      return;
    }
    fetchCourses();
    fetchEnrolledCourses();
  }, [token, navigate]);

  const fetchCourses = async () => {
    try {
      const response = await axios.get(`${API}/courses/`);
      setCourses(response.data.courses || []);
    } catch (error) {
      console.error('Error fetching courses:', error);
      toast.error('Failed to load courses');
    } finally {
      setLoading(false);
    }
  };

  const fetchEnrolledCourses = async () => {
    try {
      const response = await axios.get(`${API}/courses/enrolled`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setEnrolledCourses(response.data.courses || []);
    } catch (error) {
      console.error('Error fetching enrolled courses:', error);
    }
  };

  const handleEnroll = async (courseId) => {
    setEnrolling(true);
    try {
      const course = courses.find(c => c.id === courseId);
      const coursePrice = course?.price || 0;
      
      // Check if course is free or paid
      if (coursePrice === 0 || coursePrice === '0' || !coursePrice) {
        // Free course - direct enrollment
        await axios.post(
          `${API}/courses/${courseId}/enroll`,
          {},
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Successfully enrolled in course!');
        fetchEnrolledCourses();
        setSelectedCourse(null);
      } else {
        // Paid course - show payment confirmation
        const confirmPayment = window.confirm(
          `Course Fee: ₹${coursePrice}\\n\\nNote: In production, this will redirect to payment gateway (Razorpay/Stripe).\\n\\nFor testing: Click OK to simulate successful payment and enroll.`
        );
        
        if (confirmPayment) {
          // Create payment transaction directly with 'paid' status (simulating successful payment)
          // In production, this would be done by payment gateway webhook
          const payment_transaction = {
            id: `PAY-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
            user_id: null, // Will be set by backend
            type: "course_enrollment",
            amount: coursePrice,
            currency: "INR",
            status: "paid", // Simulating successful payment
            metadata: {
              course_id: courseId,
              course_title: course.title,
              mock_mode: true
            }
          };
          
          // Complete enrollment with mock payment
          await axios.post(
            `${API}/courses/${courseId}/enroll`,
            { 
              payment_id: payment_transaction.id,
              mock_payment: payment_transaction 
            },
            { headers: { Authorization: `Bearer ${token}` } }
          );
          
          toast.success('Payment successful! You are now enrolled.');
          fetchEnrolledCourses();
          setSelectedCourse(null);
        }
      }
    } catch (error) {
      console.error('Enrollment error:', error);
      toast.error(error.response?.data?.detail || 'Failed to enroll in course');
    } finally {
      setEnrolling(false);
    }
  };

  const categories = ['All', 'Technology', 'Business & Management', 'Business & Marketing', 'Design', 'Finance', 'Healthcare', 'Science'];

  const filteredCourses = courses.filter(course => {
    const matchesSearch = course.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         course.description?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === 'All' || course.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const isEnrolled = (courseId) => enrolledCourses.some(c => c.id === courseId);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50">
        <Navbar />
        <div className="flex items-center justify-center h-screen">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-muted-foreground">Loading courses...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50">
      <Navbar />
      <div className="max-w-7xl mx-auto px-4 py-6 md:py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-4 mb-4">
            <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-primary to-primary/80 flex items-center justify-center shadow-md">
              <BookOpen className="w-7 h-7 text-white" />
            </div>
            <div>
              <h1 className="text-3xl md:text-4xl font-bold text-gray-900">Explore Courses</h1>
              <p className="text-gray-600 text-sm md:text-base">Discover high-demand courses to advance your career</p>
            </div>
          </div>
        </div>

        {/* Search and Filters */}
        <Card className="mb-6 border-none shadow-md">
          <CardContent className="pt-6">
            <div className="space-y-4">
              <input
                type="text"
                placeholder="Search courses..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
              />
              <div className="flex gap-2 flex-wrap">
                {categories.map(cat => (
                  <Button
                    key={cat}
                    variant={selectedCategory === cat ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setSelectedCategory(cat)}
                  >
                    {cat}
                  </Button>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          <Card className="border-none shadow-md">
            <CardContent className="p-6">
              <div className="flex items-center gap-3">
                <BookOpen className="w-10 h-10 text-primary" />
                <div>
                  <p className="text-2xl font-bold">{courses.length}</p>
                  <p className="text-sm text-muted-foreground">Total Courses</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border-none shadow-md">
            <CardContent className="p-6">
              <div className="flex items-center gap-3">
                <Users className="w-10 h-10 text-success" />
                <div>
                  <p className="text-2xl font-bold">{enrolledCourses.length}</p>
                  <p className="text-sm text-muted-foreground">Enrolled Courses</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border-none shadow-md">
            <CardContent className="p-6">
              <div className="flex items-center gap-3">
                <Star className="w-10 h-10 text-warning" />
                <div>
                  <p className="text-2xl font-bold">{courses.filter(c => c.is_popular).length}</p>
                  <p className="text-sm text-muted-foreground">Popular Courses</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Courses Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredCourses.length > 0 ? (
            filteredCourses.map((course) => (
              <Card key={course.id} className="border-none shadow-md hover:shadow-xl transition-shadow cursor-pointer group">
                <div className="h-2 bg-gradient-to-r from-primary to-primary/60"></div>
                <CardHeader>
                  <div className="flex items-start justify-between mb-2">
                    <Badge variant="outline">{course.category}</Badge>
                    {course.is_popular && (
                      <Badge className="bg-warning/10 text-warning border-warning/20">
                        <Star className="w-3 h-3 mr-1" />
                        Popular
                      </Badge>
                    )}
                  </div>
                  <CardTitle className="text-lg group-hover:text-primary transition-colors">
                    {course.title}
                  </CardTitle>
                  <CardDescription className="line-clamp-2">
                    {course.description}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3 mb-4">
                    {course.duration && (
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <Clock className="w-4 h-4" />
                        <span>{course.duration}</span>
                      </div>
                    )}
                    {course.level && (
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <Target className="w-4 h-4" />
                        <span>{course.level}</span>
                      </div>
                    )}
                    {course.average_salary_india && (
                      <div className="flex items-center gap-2 text-sm font-semibold text-green-600">
                        <DollarSign className="w-4 h-4" />
                        <span>{course.average_salary_india}</span>
                      </div>
                    )}
                  </div>
                  
                  <Button
                    onClick={() => setSelectedCourse(course)}
                    className="w-full group-hover:bg-primary group-hover:text-white transition-colors"
                    variant={isEnrolled(course.id) ? 'outline' : 'default'}
                  >
                    {isEnrolled(course.id) ? (
                      <><Check className="w-4 h-4 mr-2" /> Enrolled</>
                    ) : (
                      <>View Details <ArrowRight className="w-4 h-4 ml-2" /></>
                    )}
                  </Button>
                </CardContent>
              </Card>
            ))
          ) : (
            <div className="col-span-full">
              <Card className="border-none shadow-md">
                <CardContent className="p-12 text-center">
                  <BookOpen className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
                  <h3 className="text-lg font-semibold mb-2">No courses found</h3>
                  <p className="text-muted-foreground">Try adjusting your search or filter</p>
                </CardContent>
              </Card>
            </div>
          )}
        </div>

        {/* Course Detail Modal */}
        <Dialog open={!!selectedCourse} onOpenChange={() => setSelectedCourse(null)}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            {selectedCourse && (
              <>
                <DialogHeader>
                  <div className="flex items-center gap-2 mb-2">
                    <Badge variant="outline">{selectedCourse.category}</Badge>
                    {selectedCourse.is_popular && (
                      <Badge className="bg-warning/10 text-warning border-warning/20">
                        <Star className="w-3 h-3 mr-1" />
                        Popular
                      </Badge>
                    )}
                  </div>
                  <DialogTitle className="text-2xl">{selectedCourse.title}</DialogTitle>
                  <DialogDescription>{selectedCourse.description}</DialogDescription>
                </DialogHeader>

                <Tabs defaultValue="overview" className="w-full">
                  <TabsList className="grid w-full grid-cols-4">
                    <TabsTrigger value="overview">Overview</TabsTrigger>
                    <TabsTrigger value="syllabus">Syllabus</TabsTrigger>
                    <TabsTrigger value="career">Career</TabsTrigger>
                    <TabsTrigger value="details">Details</TabsTrigger>
                  </TabsList>

                  {/* Overview Tab */}
                  <TabsContent value="overview" className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="p-4 bg-muted/30 rounded-lg">
                        <div className="flex items-center gap-2 mb-2">
                          <Clock className="w-5 h-5 text-primary" />
                          <span className="font-semibold">Duration</span>
                        </div>
                        <p className="text-sm text-muted-foreground">{selectedCourse.duration || 'N/A'}</p>
                      </div>
                      <div className="p-4 bg-muted/30 rounded-lg">
                        <div className="flex items-center gap-2 mb-2">
                          <Target className="w-5 h-5 text-primary" />
                          <span className="font-semibold">Level</span>
                        </div>
                        <p className="text-sm text-muted-foreground">{selectedCourse.level || 'N/A'}</p>
                      </div>
                    </div>

                    {/* Course Fee - NEW */}
                    <div className="p-4 bg-blue-50 rounded-lg border-2 border-blue-200">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <DollarSign className="w-6 h-6 text-blue-600" />
                          <span className="font-semibold text-lg">Course Fee</span>
                        </div>
                        <div className="text-right">
                          <p className="text-2xl font-bold text-blue-600">
                            {selectedCourse.price !== undefined && selectedCourse.price !== null 
                              ? (selectedCourse.price === 0 || selectedCourse.price === '0' 
                                  ? 'FREE' 
                                  : `₹${selectedCourse.price}`)
                              : 'Not Available'}
                          </p>
                          {selectedCourse.price > 0 && (
                            <p className="text-xs text-gray-500 mt-1">One-time payment</p>
                          )}
                        </div>
                      </div>
                    </div>

                    {selectedCourse.prerequisites && selectedCourse.prerequisites.length > 0 && (
                      <div>
                        <h4 className="font-semibold mb-2 flex items-center gap-2">
                          <GraduationCap className="w-5 h-5" />
                          Prerequisites
                        </h4>
                        <div className="flex flex-wrap gap-2">
                          {selectedCourse.prerequisites.map((prereq, idx) => (
                            <Badge key={idx} variant="secondary">{prereq}</Badge>
                          ))}
                        </div>
                      </div>
                    )}

                    {selectedCourse.skills_gained && selectedCourse.skills_gained.length > 0 && (
                      <div>
                        <h4 className="font-semibold mb-2 flex items-center gap-2">
                          <Award className="w-5 h-5" />
                          Skills You'll Gain
                        </h4>
                        <div className="flex flex-wrap gap-2">
                          {selectedCourse.skills_gained.map((skill, idx) => (
                            <Badge key={idx} variant="outline" className="bg-primary/10 text-primary">
                              {skill}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    )}
                  </TabsContent>

                  {/* Syllabus Tab */}
                  <TabsContent value="syllabus" className="space-y-4">
                    {selectedCourse.syllabus_overview && selectedCourse.syllabus_overview.length > 0 ? (
                      <div>
                        <h4 className="font-semibold mb-3">Course Syllabus</h4>
                        <div className="space-y-2">
                          {selectedCourse.syllabus_overview.map((topic, idx) => (
                            <div key={idx} className="flex items-start gap-3 p-3 bg-muted/30 rounded-lg">
                              <Check className="w-5 h-5 text-success mt-0.5 flex-shrink-0" />
                              <span className="text-sm">{topic}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    ) : (
                      <p className="text-muted-foreground text-center py-8">No syllabus information available</p>
                    )}
                  </TabsContent>

                  {/* Career Tab */}
                  <TabsContent value="career" className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                        <div className="flex items-center gap-2 mb-2">
                          <DollarSign className="w-5 h-5 text-green-600" />
                          <span className="font-semibold">Salary (India)</span>
                        </div>
                        <p className="text-lg font-bold text-green-600">{selectedCourse.average_salary_india || 'N/A'}</p>
                      </div>
                      <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                        <div className="flex items-center gap-2 mb-2">
                          <DollarSign className="w-5 h-5 text-blue-600" />
                          <span className="font-semibold">Salary (Global)</span>
                        </div>
                        <p className="text-lg font-bold text-blue-600">{selectedCourse.average_salary_global || 'N/A'}</p>
                      </div>
                    </div>

                    {selectedCourse.career_paths && selectedCourse.career_paths.length > 0 && (
                      <div>
                        <h4 className="font-semibold mb-3 flex items-center gap-2">
                          <Briefcase className="w-5 h-5" />
                          Career Paths
                        </h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                          {selectedCourse.career_paths.map((path, idx) => (
                            <div key={idx} className="flex items-center gap-2 p-2 bg-muted/30 rounded">
                              <ArrowRight className="w-4 h-4 text-primary" />
                              <span className="text-sm">{path}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {selectedCourse.top_recruiters && selectedCourse.top_recruiters.length > 0 && (
                      <div>
                        <h4 className="font-semibold mb-3 flex items-center gap-2">
                          <TrendingUp className="w-5 h-5" />
                          Top Recruiters
                        </h4>
                        <div className="flex flex-wrap gap-2">
                          {selectedCourse.top_recruiters.map((recruiter, idx) => (
                            <Badge key={idx} variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
                              {recruiter}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    )}
                  </TabsContent>

                  {/* Details Tab */}
                  <TabsContent value="details" className="space-y-4">
                    {selectedCourse.certifications && selectedCourse.certifications.length > 0 && (
                      <div>
                        <h4 className="font-semibold mb-3 flex items-center gap-2">
                          <Award className="w-5 h-5" />
                          Industry Certifications
                        </h4>
                        <div className="space-y-2">
                          {selectedCourse.certifications.map((cert, idx) => (
                            <div key={idx} className="flex items-center gap-2 p-3 bg-amber-50 rounded-lg border border-amber-200">
                              <Award className="w-4 h-4 text-amber-600" />
                              <span className="text-sm font-medium">{cert}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </TabsContent>
                </Tabs>

                <DialogFooter>
                  <Button variant="outline" onClick={() => setSelectedCourse(null)}>Close</Button>
                  {!isEnrolled(selectedCourse.id) && (
                    <Button onClick={() => handleEnroll(selectedCourse.id)} disabled={enrolling}>
                      {enrolling ? 'Enrolling...' : 'Enroll Now'}
                    </Button>
                  )}
                </DialogFooter>
              </>
            )}
          </DialogContent>
        </Dialog>
      </div>
      <Footer />
    </div>
  );
};

export default Courses;
